import { z } from 'zod';
import { AccreditationCodeSchema } from './cer-portal.js';
/**
 * What the Chrome extension reports back after every attempt to fill a portal
 * form — success, partial, or failure.
 *
 * WHY THIS EXISTS
 * ---------------
 * Until this endpoint, the only server-side trace of a fill was the
 * `portal-payload.read` audit event, which is written *after* the extension
 * successfully fetches an approved payload. Every failure mode before that
 * point — no accreditation code on the page, expired session, content script
 * not attached, page structure changed — left no record anywhere. "The fill
 * didn't work" could only be investigated as an absence of logs, which cannot
 * distinguish "never tried" from "tried and failed".
 *
 * So: one event per attempt, written whatever the outcome, including the
 * attempts that never got as far as knowing which job they were for.
 *
 * DELIBERATELY NOT PII. The portal form holds an owner's name, address, and
 * equipment; none of it belongs in a telemetry record. This carries counts,
 * enum outcomes, and the accreditation code — nothing read off the page.
 */
export const PortalFillAttemptSchema = z.object({
    /** Matches the side panel's own three-state outcome. */
    status: z.enum(['ok', 'warn', 'error']),
    /**
     * Absent when the attempt failed before a code was resolved — which is
     * exactly the case this endpoint exists to make visible.
     */
    jobId: AccreditationCodeSchema.optional(),
    /** Whether the code came off the page or the inspector typed it. */
    jobIdSource: z.enum(['page', 'manual']).optional(),
    /** Machine-readable reason, e.g. `NO_JOB_ID`, `BAD_JOB_ID`, `NOT_FOUND`. */
    code: z.string().min(1).max(64).optional(),
    /** The message the inspector saw, so the log matches their description of it. */
    message: z.string().min(1).max(500),
    /** Which portal the tab was on. */
    portal: z.enum(['cer', 'itp']),
    /** Wizard step, when the content script could determine one. */
    page: z.string().min(1).max(64).optional(),
    /** Fields successfully written / attempted. Absent when the fill never ran. */
    written: z.number().int().nonnegative().optional(),
    total: z.number().int().nonnegative().optional(),
    /** Diagnostic field IDs only, never field values or attachment filenames. */
    failedFields: z
        .array(z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-zA-Z0-9_. -]+$/))
        .max(200)
        .optional(),
    /** `manifest.json` version, so a stale install is obvious in the audit trail. */
    extensionVersion: z.string().min(1).max(32).optional(),
});
/** Audit `action` written for every reported attempt. */
export const PORTAL_FILL_ATTEMPT_ACTION = 'portal-fill.attempt';
//# sourceMappingURL=portal-fill.js.map